/**
 * Where the back office lives. Pure helpers shared by src/proxy.ts and the
 * (platform) route group; no server imports so the proxy (edge) can use them.
 *
 * Production: admin.{ROOT_DOMAIN} → rewritten to /admin/… by the proxy.
 * Path-routed hosts (localhost, Vercel previews): /admin/… directly.
 * `admin` is already in RESERVED_SLUGS, so getSubdomain() in the proxy
 * returns null for it and it can never collide with a congregation.
 */
//
// Namespace note: /admin/<page> that has no route in (platform)/admin falls
// through to (admin)/[tenant] with tenant="admin", which 404s (no such tenant,
// slug reserved). Safe, but don't add a tenant page and a platform page with
// the same name and expect both to resolve.
export const PLATFORM_HOST_SLUG = 'admin'
export const PLATFORM_PATH_PREFIX = '/admin'

/** True only for `admin.<rootDomain>` (port ignored). */
export function isPlatformHost(host: string, rootDomain: string): boolean {
    const hostname = host.split(':')[0]
    const root = rootDomain.split(':')[0]
    return hostname === `${PLATFORM_HOST_SLUG}.${root}`
}

/** True for `/admin` and `/admin/…` (whole first segment, not a prefix match). */
export function isPlatformPath(path: string): boolean {
    return path === PLATFORM_PATH_PREFIX || path.startsWith(`${PLATFORM_PATH_PREFIX}/`)
}

/**
 * Absolute URL of the back-office home for the incoming request host.
 * Mirrors the proxy's root-path logic: subdomain on the real domain, path on
 * localhost and previews (a subdomain redirect would leave the preview).
 */
export function platformHomeUrl(host: string, rootDomain: string, protocol: string): string {
    const hostname = host.split(':')[0]
    const root = rootDomain.split(':')[0]
    const onRootDomain = hostname === root || hostname.endsWith(`.${root}`)
    const isLocal = hostname.includes('localhost') || hostname === '127.0.0.1'
    if (onRootDomain && !isLocal) {
        return `${protocol}//${PLATFORM_HOST_SLUG}.${root}/`
    }
    return `${protocol}//${host}${PLATFORM_PATH_PREFIX}`
}
